import Cookies from "js-cookie";
import telemetryIO from "@teaming/io-telemetry";
import { foundation } from "@teaming/slice-engine";

let instance;

import {
	AuthenticationEvents,
	CalendarSyncEvents,
	OnboardingType
} from "@teaming/common-enums";

export default function telemetryFactory( { config, } ) {
	if ( !instance ) {
		instance = telemetryIO( { config, foundation, } );
	}
	return instance;
}

const marketingCookie = {
	// eslint-disable-next-line no-magic-numbers
	cookieDomain: ( window.location.hostname.split( "." ).slice( -2 ).join( "." ) ),
	cookieKeys: [ "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", ],
	getValues() {
		const domain = `.${ this.cookieDomain }`;
		return this.cookieKeys.reduce( ( acc, key ) => {
			const value = ( Cookies.get( key, { domain, } ) || "" ).trim();
			if ( value ) {
				acc[ key ] = value;
			}
			return acc;
		}, {} );
	},
	clearValues() {
		const domain = `.${ this.cookieDomain }`;
		this.cookieKeys.forEach( key => {
			Cookies.remove( key, { domain, } );
		} );
	},
};

export function wireUpTelemetryToEvents() {
	const authService = foundation().auth;
	// eslint-disable-next-line max-statements
	async function setUserAttributesAndScopeToUser( { userProfile, } ) {
		let customAttributes = {};
		const auth0User = await authService.lookupUser();

		const {
			personId,
			onboarding,
			freeTrialExpiresAt,
			showPaymentOption,
		} = userProfile;

		const {
			signupTeamId = "",
			teamingSampleOneOnOneId = "",
			type: onboardingType,
		} = onboarding;

		const isVerifiedUser = ( signupTeamId && auth0User.email_verified );
		let clearMarketingCookie = false;

		customAttributes[ "Trial Expiration Date" ] = freeTrialExpiresAt;
		customAttributes[ "Is Licensed User" ] = !showPaymentOption;
		customAttributes[ "Sample One on One ID" ] = teamingSampleOneOnOneId;

		/* eslint-disable camelcase */
		if ( signupTeamId ) {
			customAttributes[ "Default Signup Team ID" ] = signupTeamId;
			// TODO: still needed?
			customAttributes.initial_signup_team_id = signupTeamId;
		}

		if ( isVerifiedUser ) {
			try {
				const { ioTeam, } = foundation();

				const promises = [
					ioTeam.fetchTeamMemberDemographics( { teamId: signupTeamId, personId, } ),
					ioTeam.fetchTeam( { teamId: signupTeamId, } ),
				];

				const [ demographicsResponse, teamResponse, ] = await Promise.all( promises );

				const { data: demoData, } = demographicsResponse;
				const { data: team, } = teamResponse;

				const { growth, teamGrowth, } = demoData;
				const { selectedOpportunity, } = growth;

				const { characteristics, } = team;
				const { teamSize, } = characteristics;

				if ( onboardingType === OnboardingType.LEAD ) {
					customAttributes.team_lead_personal_growth_priority = selectedOpportunity;
					customAttributes.team_lead_initial_team_growth_priority = teamGrowth;
				} else {
					customAttributes.team_member_personal_growth_priority = selectedOpportunity;
				}

				customAttributes[ "Indicated Team Size" ] = teamSize;

				customAttributes = {
					...customAttributes,
					...( marketingCookie.getValues() ),
				};

				clearMarketingCookie = true;
			} catch {
				// there's really nothing to be done here
				// we either got a 404 because this user hasn't answered the onboarding questions yet
				// or we got some other kind of error, but we still need to allow the intercom call to fire
			}
		}
		/* eslint-enable camelcase */

		instance.scopeToUser( userProfile, customAttributes );

		if ( clearMarketingCookie ) {
			marketingCookie.clearValues();
		}
	}

	const formatSyncSource = source => {
		return source === "GOOGLE" ? "Google" : "Microsoft";
	};

	authService.on(
		AuthenticationEvents.LOGGING_IN,
		async () => {
			instance.session.signingIn();
		}
	);

	authService.on(
		AuthenticationEvents.LOGGING_OUT,
		async () => {
			instance.session.signingOut();
		}
	);

	authService.on(
		AuthenticationEvents.LOGIN,
		async ( { userProfile, } ) => {
			await setUserAttributesAndScopeToUser( { userProfile, } );
			instance.session.signInCompleted();
		}
	);

	authService.on(
		AuthenticationEvents.LOGIN_ERROR,
		( { error, } ) => { instance.session.signInFailed( error.message ); }
	);

	authService.on(
		AuthenticationEvents.SIGNUP,
		async ( { userProfile, } ) => {
			await setUserAttributesAndScopeToUser( { userProfile, } );
			instance.session.signUpCompleted();
		}
	);

	authService.on(
		AuthenticationEvents.SIGNUP_ERROR,
		( { error, } ) => { instance.session.signUpFailed( error.message ); }
	);

	authService.on( CalendarSyncEvents.PERMISSION_REQUEST_INITIATED, ( { source, } ) => {
		instance.onboarding.calendarAuthorizationInitiated(
			formatSyncSource( source ),
			"Onboarding"
		);
	} );

	authService.on( CalendarSyncEvents.PERMISSION_REQUEST_COMPLETED, ( { source, } ) => {
		instance.onboarding.calendarAuthorizationComplete(
			formatSyncSource( source ),
			"succeeded",
			"Onboarding"
		);
	} );

	authService.on( CalendarSyncEvents.PERMISSION_REQUEST_ERROR, ( { source, } ) => {
		instance.onboarding.calendarAuthorizationComplete(
			formatSyncSource( source ),
			"failed",
			"Onboarding"
		);
	} );

	authService.on(
		AuthenticationEvents.LOGOUT,
		() => {
			// cleans up all telemetry stuff
			instance.dispose();
		}
	);
}
